import PropTypes from "prop-types";
import React, { useContext } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { SidebarContext } from ".";
import { createAppBasePath } from "../../../../consts/route-consts";
import { useAppInfo } from "../../../../helpers/hooks/common-hook";
import { cn } from "../../../../helpers/utils/css-utils";
import { stmNodeSelectRest } from "../../../../redux/journey-toolbox/journey-toolbox-actions";

function MenuItem({
  name,
  href,
  icon,
  tip,
  active,
  appPath = true,
  nested = false,
  stmNodeSelectRest,
  resetContext,
  onClick,
  badge,
  flash = false,
}) {
  const appInfo = useAppInfo();
  const { expanded } = useContext(SidebarContext);
  const navigate = useNavigate();

  let navPath = href;

  if (appPath && appInfo) {
    navPath = `${createAppBasePath(appInfo.appId)}${href}`;
  }

  const onClickNav = (e) => {
    // Action rows (e.g. Publish) run their handler instead of navigating.
    if (onClick) {
      e.preventDefault();
      onClick();
      return;
    }
    stmNodeSelectRest();
    if (resetContext) {
      window.location = navPath;
    } else {
      navigate(navPath);
    }
  };

  // Icons come from the nav config as bare elements; size and color are owned
  // here so every row in the rail stays visually identical.
  const renderedIcon = React.cloneElement(icon, {
    className: cn(
      icon.props.className,
      "transition-colors duration-150",
      nested ? "w-[15px] h-[15px]" : "w-[18px] h-[18px]"
    ),
  });

  return (
    // eslint-disable-next-line jsx-a11y/anchor-is-valid
    <a
      onClick={onClickNav}
      href="#"
      title={!expanded ? undefined : tip}
      aria-current={active ? "page" : undefined}
      className={cn(
        `group relative flex items-center gap-2.5 rounded-lg outline-none
         transition-colors duration-150
         focus-visible:ring-2 focus-visible:ring-accent/40`,
        nested ? "py-1.5 pl-2 pr-2" : "py-2 px-2.5",
        // the copilot just changed something behind this item
        flash && "animate-copilot-flash",
        active
          ? "bg-panel text-ink shadow-sm ring-1 ring-ink/5"
          : "text-ink/80 hover:bg-raised/60 hover:text-ink"
      )}
    >
      {/* Active indicator bar, anchored to the rail's left edge */}
      <span
        aria-hidden="true"
        className={cn(
          "absolute left-0 top-1/2 -translate-y-1/2 w-[3px] rounded-r-full transition-all duration-200",
          active
            ? "h-4 bg-accent opacity-100"
            : "h-0 bg-transparent opacity-0"
        )}
      />

      <span
        className={cn(
          "flex flex-none items-center justify-center transition-colors duration-150",
          nested ? "w-4 h-4" : "w-[18px] h-[18px]",
          active
            ? "text-ink"
            : "text-muted group-hover:text-ink"
        )}
      >
        {renderedIcon}
      </span>

      <span
        className={cn(
          "min-w-0 flex-1 overflow-hidden truncate transition-all duration-200",
          nested ? "text-[13px]" : "text-[13.5px]",
          active ? "font-semibold" : "font-medium",
          { "w-0 opacity-0": !expanded }
        )}
      >
        {name}
      </span>

      {expanded && badge}

      {/* Collapsed-rail tooltip */}
      {!expanded && (
        <span
          className="pointer-events-none invisible absolute left-full z-50 ml-3 -translate-x-2 whitespace-nowrap
                     rounded-md bg-accent px-2 py-1 text-xs font-medium text-accent-ink opacity-0 shadow-lg
                     transition-all duration-150 group-hover:visible group-hover:translate-x-0 group-hover:opacity-100"
        >
          {name}
        </span>
      )}
    </a>
  );
}

MenuItem.propTypes = {
  name: PropTypes.string,
  href: PropTypes.string,
  icon: PropTypes.element,
  tip: PropTypes.string,
  active: PropTypes.bool,
  appPath: PropTypes.bool,
  nested: PropTypes.bool,
  onClick: PropTypes.func,
  badge: PropTypes.node,
  flash: PropTypes.bool,
};

const mapsStateToProps = (state, ownProps) => {
  return {};
};

const mapDispatchToProps = (dispatch) => {
  return {
    stmNodeSelectRest: (message) => {
      dispatch(stmNodeSelectRest(message));
    },
  };
};

export default connect(mapsStateToProps, mapDispatchToProps)(MenuItem);
